import { v4 as uuidv4 } from 'uuid';
import {
  beginBackgroundTurnHandoff,
  markBackgroundTurnResumeStarted,
} from './background-turn-handoff';
import { useChatInstancesStore } from './chatInstancesStore';
import {
  BACKGROUND_TURN_SHELL_ID_PREFIX,
  clearBusyCopilotFollowUps,
  messagesForBackgroundTurn,
  readBusyCopilotFollowUps,
  readUserMessagePlacements,
} from './copilot-pending-follow-up';

const HANDOFF_SETTLE_MS = 1_000;

export async function releaseOpenCopilotStream({
  chatId,
  markResumeStarted,
}: {
  chatId: string;
  markResumeStarted: 'before-stop' | 'after-settle';
}): Promise<void> {
  const chat = useChatInstancesStore.getState().chats.get(chatId);

  if (!chat || (chat.status !== 'streaming' && chat.status !== 'submitted')) {
    return;
  }

  const settled = beginBackgroundTurnHandoff(chatId);

  if (markResumeStarted === 'before-stop') {
    markBackgroundTurnResumeStarted(chatId);
  }

  try {
    await chat.stop();
  } catch (error: unknown) {
    console.warn(
      'Failed to release the open copilot stream before the next turn; continuing',
      error,
    );
  }

  await Promise.race([
    settled,
    new Promise<void>((resolve) => {
      setTimeout(resolve, HANDOFF_SETTLE_MS);
    }),
  ]);
  markBackgroundTurnResumeStarted(chatId);
}

export async function resumeBackgroundCopilotTurn(
  chatId: string,
): Promise<void> {
  const { chats, configs } = useChatInstancesStore.getState();
  const chat = chats.get(chatId);
  const config = configs.get(chatId);
  const setMessages = config?.setMessages;
  const resumeStream = config?.resumeStream;

  if (!chat || !setMessages || !resumeStream) {
    return;
  }

  await releaseOpenCopilotStream({ chatId, markResumeStarted: 'after-settle' });

  const currentChat = useChatInstancesStore.getState().chats.get(chatId);
  const { followUps } = readBusyCopilotFollowUps(chatId);

  setMessages(
    messagesForBackgroundTurn({
      messages: currentChat?.messages ?? chat.messages,
      followUps,
      predecessorIdsByMessageId: readUserMessagePlacements(chatId),
      shellId: `${BACKGROUND_TURN_SHELL_ID_PREFIX}${chatId}:${uuidv4()}`,
    }),
  );
  clearBusyCopilotFollowUps(chatId);
  await Promise.resolve(resumeStream());
}
